import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'opt-client-udf-field',
  templateUrl: './client-udf-field.component.html',
  styleUrls: ['./client-udf-field.component.scss']
})
export class ClientUdfFieldComponent implements OnInit, OnChanges {

  @Input() type?: string;
  @Input() placeholder: string = 'Enter value';
  @Input() label?: string;
  @Input() options: any[] = [];
  @Input() udfId?: string;
  @Input() initialValue?: string;
  @Input() required: boolean = false;
  @Input() parentForm?: FormGroup;
  @Input() controlName?: string;

  @Output() valueChange = new EventEmitter<string>();

  udfForm!: FormGroup;
  filteredOptions: any[] = [];
  searchControl = new FormControl();
  displayLabel: string;
  resolvedControlName: string = 'value';

  constructor(
    private fb: FormBuilder
  ) { }

  getLowerType(): string {
    return (this.type || '').toString().trim().toLowerCase();
  }

  private ensureControlExists() {
    const validators = this.required ? [Validators.required] : [];

    // Normalize initial value for date fields
    let normalizedInitial: any = this.initialValue || '';
    const t = this.getLowerType();
    if (t === 'date' || t === 'datepicker') {
      if (typeof normalizedInitial === 'string' && normalizedInitial) {
        const d = new Date(normalizedInitial);
        if (!isNaN(d.getTime())) normalizedInitial = d;
      }
    }

    if (this.parentForm && this.controlName) {
      this.resolvedControlName = this.controlName;
      const existing = this.parentForm.get(this.controlName);
      if (!existing) {
        this.parentForm.addControl(this.controlName, new FormControl(normalizedInitial, validators));
      } else {
        existing.setValidators(validators);
        if (normalizedInitial !== undefined && normalizedInitial !== null) {
          existing.setValue(normalizedInitial);
        }
        existing.updateValueAndValidity({ emitEvent: false });
      }
    } else {
      this.resolvedControlName = 'value';
      this.udfForm = this.fb.group({
        value: [normalizedInitial, validators],
      });
    }
  }

  ngOnInit(): void {
    this.displayLabel = this.label || this.placeholder || '';

    this.ensureControlExists();

    this.filterOptions('');
    this.searchControl.valueChanges.subscribe((search: string) => {
      this.filterOptions(search || '');
    });

    const ctrl = this.getControl();
    if (ctrl && (this.initialValue !== undefined && this.initialValue !== null && this.initialValue !== '')) {
      this.valueChange.emit(String(this.initialValue));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['label'] || changes['placeholder']) {
      this.displayLabel = this.label || this.placeholder || '';
    }

    if (changes['parentForm'] || changes['controlName'] || changes['required']) {
      this.ensureControlExists();
    }

    if (changes['initialValue']) {
      const ctrl = this.getControl();
      if (ctrl) {
        let normalizedValue: any = this.initialValue || '';
        const t = this.getLowerType();
        if (t === 'date' || t === 'datepicker') {
          if (typeof normalizedValue === 'string' && normalizedValue) {
            const d = new Date(normalizedValue);
            if (!isNaN(d.getTime())) {
              normalizedValue = d;
            }
          }
        }
        ctrl.setValue(normalizedValue);
        ctrl.updateValueAndValidity({ emitEvent: false });
      }
    }

    if (changes['options']) {
      this.filterOptions(this.searchControl?.value || '');
    }
  }

  private getControl(): FormControl | null {
    if (this.parentForm && this.controlName) {
      return this.parentForm.get(this.controlName) as FormControl;
    }
    return this.udfForm?.get('value') as FormControl;
  }

  filterOptions(value: string) {
    const filterValue = (value || '').toLowerCase();

    if (filterValue !== '') {
      this.filteredOptions = (this.options || [])
        .filter(option => (option?.name || '').toLowerCase().includes(filterValue))
        .sort((a, b) => {
          const aName = (a?.name || '').toLowerCase();
          const bName = (b?.name || '').toLowerCase();
          const aStartsWith = aName.startsWith(filterValue) ? 0 : 1;
          const bStartsWith = bName.startsWith(filterValue) ? 0 : 1;

          if (aStartsWith !== bStartsWith) {
            return aStartsWith - bStartsWith;
          }

          return aName.localeCompare(bName);
        });
    } else {
      this.filteredOptions = (this.options || []).slice().sort((a, b) =>
        (a?.name || '').localeCompare(b?.name || '')
      );
    }
  }

  onUDFChange(): void {
    const ctrl = this.getControl();
    const selectedValue = ctrl ? ctrl.value : undefined;
    this.valueChange.emit(selectedValue);
  }

  getErrorMessage(): string {
    const ctrl = this.getControl();
    if (ctrl?.hasError('required')) {
      return `${this.displayLabel} is required`;
    }
    return '';
  }
}

